import { AlertTriangle, BellRing, CheckCircle, Pill, Tags } from "lucide-react";

export const TRANSMISSION_TAGS = [
  ["fatigue", "Fatigue"], ["pain", "Douleur"], ["nausea", "Nausée"], ["vomiting", "Vomissement"],
  ["diarrhea", "Diarrhée"], ["constipation", "Constipation"], ["fever", "Fièvre"], ["dyspnea", "Dyspnée"],
  ["cough", "Toux"], ["mouth_sores", "Mucite"], ["neuropathy", "Neuropathie"], ["rash", "Rash"],
  ["bleeding", "Saignement"], ["edema", "Œdème"], ["oral_adherence", "Observance"], ["missed_dose", "Dose oubliée"],
  ["treatment_tolerance", "Tolérance traitement"], ["hospitalization", "Hospitalisation"], ["appointment_need", "RDV"], ["callback_need", "Rappel"],
];

export const PRIORITY_OPTIONS = [
  ["INFO", "Info", "bg-slate-100 text-slate-700 border-slate-200", CheckCircle],
  ["WATCH", "À surveiller", "bg-amber-50 text-amber-800 border-amber-200", BellRing],
  ["ALERT", "Alerte", "bg-orange-50 text-orange-800 border-orange-200", AlertTriangle],
  ["CRITICAL", "Critique", "bg-red-50 text-red-800 border-red-200", AlertTriangle],
];

export const ACTION_OPTIONS = [
  ["NONE", "Aucune action"],
  ["CALL_PATIENT", "Rappeler patient"],
  ["DOCTOR_REVIEW", "Avis médecin"],
  ["APPOINTMENT", "RDV à prévoir"],
  ["SECRETARY_TASK", "Tâche secrétaire"],
  ["ADHERENCE_REVIEW", "Revoir observance"],
  ["PROTOCOL_REVIEW", "Revoir protocole"],
  ["URGENT_ESCALATION", "Escalade urgente"],
];

export default function TransmissionStructuredFields({
  priority, setPriority, clinicalTags, setClinicalTags, actionRequired, setActionRequired,
  treatmentRelated, setTreatmentRelated, oralAdherenceRelated, setOralAdherenceRelated,
  prefix = "transmission",
}) {
  const toggleTag = (tag) => {
    setClinicalTags((prev) => prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag]);
  };

  return (
    <div className="space-y-4" data-testid={`${prefix}-structured-fields`}>
      <div>
        <label className="mb-2 block text-sm font-medium text-slate-600">Priorité data</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid={`${prefix}-priority-group`}>
          {PRIORITY_OPTIONS.map(([value, label, tone, Icon]) => {
            const active = priority === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setPriority(value)}
                data-testid={`${prefix}-priority-${value}`}
                className={`inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-bold transition-[background-color,border-color,color,transform] active:scale-[0.98] ${active ? tone : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-600"><Tags className="h-4 w-4" /> Tags cliniques RWE</label>
        <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50 p-2" data-testid={`${prefix}-clinical-tags-group`}>
          {TRANSMISSION_TAGS.map(([value, label]) => {
            const active = clinicalTags.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleTag(value)}
                data-testid={`${prefix}-tag-${value}`}
                className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-[background-color,border-color,color] ${active ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-600">Action attendue</label>
        <select
          value={actionRequired}
          onChange={(event) => setActionRequired(event.target.value)}
          data-testid={`${prefix}-action-required-select`}
          className="min-h-[42px] w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none transition-[border-color,box-shadow] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
        >
          {ACTION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      <div className="grid gap-2 sm:grid-cols-2" data-testid={`${prefix}-relation-flags`}>
        <label className="flex min-h-[42px] items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={treatmentRelated}
            onChange={(event) => setTreatmentRelated(event.target.checked)}
            data-testid={`${prefix}-treatment-related-checkbox`}
            className="h-4 w-4 accent-emerald-600"
          />
          <Pill className="h-4 w-4 text-emerald-600" /> Lié au traitement
        </label>
        <label className="flex min-h-[42px] items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={oralAdherenceRelated}
            onChange={(event) => setOralAdherenceRelated(event.target.checked)}
            data-testid={`${prefix}-oral-adherence-related-checkbox`}
            className="h-4 w-4 accent-emerald-600"
          />
          Observance per os
        </label>
      </div>
    </div>
  );
}